import { useEffect, useState } from "react";
import { Sparkles, Activity } from "lucide-react";
import { weeklyUpdatesApi } from "@/lib/api";

/* Small, focused panel that lives inside the project view. Read-only —
   admins go to /admin/weekly-updates to trigger new batches. */

const HEALTH = {
  on_track: { label: "On Track", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  at_risk:  { label: "At Risk",  cls: "bg-amber-500/15   text-amber-300   border-amber-500/40" },
  delayed:  { label: "Delayed",  cls: "bg-rose-500/15    text-rose-300    border-rose-500/40" },
};

export default function ProjectWeeklyUpdatesPanel({ projectId }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const data = await weeklyUpdatesApi.listForProject(projectId);
        setUpdates(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  if (loading) {
    return (
      <div className="text-sm text-zinc-500" data-testid="project-updates-loading">
        Loading updates…
      </div>
    );
  }

  if (!updates.length) {
    return (
      <div
        data-testid="project-updates-empty"
        className="border border-dashed border-zinc-800 rounded-sm p-8 text-center text-sm text-zinc-500"
      >
        <Sparkles className="w-5 h-5 text-zinc-700 mx-auto mb-2" />
        No weekly updates yet. New updates generate automatically every Monday at 8am.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="project-updates-panel">
      {updates.map((u) => {
        const health = HEALTH[u.health_status] || HEALTH.on_track;
        const m = u.metrics || {};
        return (
          <div
            key={u.id}
            data-testid={`project-update-${u.id}`}
            className="border border-zinc-800 rounded-sm p-5 bg-zinc-950"
          >
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="text-overline text-zinc-500">
                  Week ending {u.week_ending}
                </div>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider border ${health.cls}`}
                >
                  {health.label}
                </span>
              </div>
              <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">
                {u.generated_at?.slice(0, 10)}
                {u.generated_by === "system" ? " · auto" : " · manual"}
              </div>
            </div>
            <div className="whitespace-pre-line text-sm leading-relaxed text-zinc-300 mb-4">
              {u.body}
            </div>
            {m.next_milestone && (
              <div className="pt-3 border-t border-zinc-900 flex items-center gap-2 text-xs text-zinc-500 font-mono">
                <Activity className="w-3 h-3" />
                Next milestone:{" "}
                <span className="text-zinc-300">{m.next_milestone.name}</span>
                {m.next_milestone.start && <span>· {m.next_milestone.start}</span>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
